import { useEffect, useMemo, useState } from "react";
import { Check, Loader2, X as XIcon } from "lucide-react";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { estApi } from "../lib/api";

const UOMS = ["ea", "none", "hrs", "ft", "sq_ft", "min", "lf", "sf"];

function Field({ label, children, hint }) {
  return (
    <label className="block">
      <span className="text-[11px] font-medium uppercase tracking-wide text-slate-500">{label}</span>
      {children}
      {hint && <span className="mt-0.5 block text-[10px] text-slate-400">{hint}</span>}
    </label>
  );
}

const inputCls = "mt-1 h-8 w-full rounded-md border border-slate-200 px-2 text-xs";

export default function ItemDrawer({ open, onOpenChange, item, options, onSaved }) {
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);
  const isNew = !item?.id;

  useEffect(() => {
    if (!open) return;
    setForm({
      item_type: item?.item_type || "part",
      item_number: item?.item_number || "",
      name: item?.name || "",
      description: item?.description || "",
      manufacturer: item?.manufacturer || "",
      catalog_path: item?.catalog_path || "",
      uom: item?.uom || "ea",
      unit_cost: item?.unit_cost ?? 0,
      labor_hours_per_unit: item?.labor_hours_per_unit ?? 0,
      taxable: item?.taxable ?? true,
      cost_code_id: item?.cost_code_id || "",
      cost_type_id: item?.cost_type_id
        || (item?.id ? "" : options?.cost_types?.find((t) => t.code === "M")?.id || ""),
      vendor: item?.vendor || "",
      lead_time_days: item?.lead_time_days ?? "",
      notes: item?.notes || "",
      active: item?.active ?? true,
    });
  }, [open, item]);

  const set = (k, v) => setForm((f) => {
    const next = { ...f, [k]: v };
    // Labor books to L, everything else to M — the estimator can still override.
    if (k === "item_type" && !f.cost_type_id) {
      const want = v === "labor" ? "L" : "M";
      next.cost_type_id = options?.cost_types?.find((t) => t.code === want)?.id || "";
    }
    return next;
  });

  const codeOf = (id) => options?.cost_codes?.find((c) => c.id === id)?.code || "";
  const typeOf = (id) => options?.cost_types?.find((t) => t.id === id)?.code || "";
  const budgetCode = form.cost_code_id && form.cost_type_id
    ? `${codeOf(form.cost_code_id)}.${typeOf(form.cost_type_id)}` : "—";

  // Procore only takes an item that books to a real budget code, so the gates are shown live.
  const checks = useMemo(() => {
    const code = options?.cost_codes?.find((c) => c.id === form.cost_code_id);
    const known = options?.budget_codes;
    return [
      { ok: !!form.name?.trim(), label: "Name" },
      { ok: !!form.uom, label: "Unit of measure" },
      { ok: form.item_type !== "part" || !!form.item_number?.trim(),
        label: "Item number (parts only)" },
      { ok: !!form.cost_code_id, label: "Cost code" },
      { ok: !!form.cost_type_id, label: "Cost type" },
      { ok: !!(code && form.cost_type_id) && (!known || known.includes(budgetCode)),
        label: `Budget code exists in Procore${budgetCode !== "—" ? ` (${budgetCode})` : ""}` },
    ];
  }, [form, options, budgetCode]);
  const ready = checks.every((c) => c.ok);

  // A cost code implies where the item sits in the tree, so show it before saving.
  const derivedCategory = useMemo(() => {
    const code = options?.cost_codes?.find((c) => c.id === form.cost_code_id);
    if (!code) return "";
    const label = String(code.name || "").split(/\s+/)
      .map((w) => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase()).join(" ");
    return `${code.tier2 || code.code} ${label}`.trim();
  }, [form.cost_code_id, options]);

  const save = async () => {
    if (!ready) return toast.error("Finish the Procore checklist first");
    setSaving(true);
    try {
      const payload = {
        ...form,
        unit_cost: parseFloat(form.unit_cost) || 0,
        labor_hours_per_unit: parseFloat(form.labor_hours_per_unit) || 0,
        lead_time_days: form.lead_time_days === "" ? null : parseInt(form.lead_time_days, 10),
        cost_code_id: form.cost_code_id || null,
        cost_type_id: form.cost_type_id || null,
      };
      const saved = isNew ? await estApi.createItem(payload) : await estApi.updateItem(item.id, payload);
      toast.success(isNew ? "Item created" : "Item saved");
      onSaved(saved);
      onOpenChange(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent className="w-[440px] sm:max-w-[440px] overflow-y-auto" data-testid="item-drawer">
        <SheetHeader>
          <SheetTitle>{isNew ? "Add Item" : "Edit Item"}</SheetTitle>
          <SheetDescription>
            {isNew ? "Manual items aren't overwritten by a Procore import." :
              "Editing a field here protects it from future Procore imports."}
          </SheetDescription>
        </SheetHeader>

        <div className="mt-4 space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <Field label="Item type">
              <select value={form.item_type} onChange={(e) => set("item_type", e.target.value)}
                      className={inputCls} data-testid="drawer-item-type">
                {["part", "custom", "labor"].map((t) => <option key={t} value={t}>{t}</option>)}
              </select>
            </Field>
            <Field label="Item number">
              <input value={form.item_number} onChange={(e) => set("item_number", e.target.value)}
                     className={inputCls} data-testid="drawer-item-number" />
            </Field>
          </div>

          <Field label="Name">
            <input value={form.name} onChange={(e) => set("name", e.target.value)}
                   className={inputCls} data-testid="drawer-name" />
          </Field>
          <Field label="Description">
            <textarea value={form.description} onChange={(e) => set("description", e.target.value)}
                      rows={2} className="mt-1 w-full rounded-md border border-slate-200 px-2 py-1 text-xs"
                      data-testid="drawer-description" />
          </Field>

          <div className="grid grid-cols-2 gap-3">
            <Field label="Manufacturer">
              <input value={form.manufacturer} onChange={(e) => set("manufacturer", e.target.value)}
                     className={inputCls} data-testid="drawer-manufacturer" />
            </Field>
            <Field label="Vendor">
              <input value={form.vendor} onChange={(e) => set("vendor", e.target.value)}
                     className={inputCls} data-testid="drawer-vendor" />
            </Field>
          </div>

          <Field label="Catalog path" hint="Backslash-delimited, drives the category tree">
            <input value={form.catalog_path} onChange={(e) => set("catalog_path", e.target.value)}
                   className={`${inputCls} font-mono`} data-testid="drawer-catalog-path" />
          </Field>

          <div className="grid grid-cols-3 gap-3">
            <Field label="UoM">
              <select value={form.uom} onChange={(e) => set("uom", e.target.value)}
                      className={inputCls} data-testid="drawer-uom">
                {UOMS.map((u) => <option key={u} value={u}>{u}</option>)}
              </select>
            </Field>
            <Field label={form.item_type === "labor" ? "Rate ($/hr)" : "Unit cost"}>
              <input type="number" step="0.01" value={form.unit_cost}
                     onChange={(e) => set("unit_cost", e.target.value)}
                     className={`${inputCls} text-right`} data-testid="drawer-unit-cost" />
            </Field>
            <Field label="Labor hrs/unit">
              <input type="number" step="0.01" value={form.labor_hours_per_unit}
                     onChange={(e) => set("labor_hours_per_unit", e.target.value)}
                     className={`${inputCls} text-right`} data-testid="drawer-labor-hours" />
            </Field>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <Field label="Cost code">
              <select value={form.cost_code_id} onChange={(e) => set("cost_code_id", e.target.value)}
                      className={inputCls} data-testid="drawer-cost-code">
                <option value="">— none —</option>
                {(options?.cost_codes || []).map((c) => (
                  <option key={c.id} value={c.id}>{c.code} {c.name}</option>
                ))}
              </select>
            </Field>
            <Field label="Cost type">
              <select value={form.cost_type_id} onChange={(e) => set("cost_type_id", e.target.value)}
                      className={inputCls} data-testid="drawer-cost-type">
                <option value="">— none —</option>
                {(options?.cost_types || []).map((t) => (
                  <option key={t.id} value={t.id}>{t.code} — {t.name}</option>
                ))}
              </select>
            </Field>
          </div>

          <div className="rounded-md border border-slate-200 bg-slate-50 px-3 py-2">
            <div className="text-[10px] uppercase tracking-wide text-slate-500">Budget code (computed)</div>
            <div className="font-mono text-sm text-slate-800" data-testid="drawer-budget-code">{budgetCode}</div>
            {derivedCategory && (
              <div className="mt-1 text-[10px] text-slate-500">
                Lands under <span className="font-medium text-slate-700">{derivedCategory}</span>
                {form.manufacturer ? ` \u203a ${form.manufacturer}` : ""} in the cost library
              </div>
            )}
          </div>

          <div className={`rounded-md border px-3 py-2 ${
            ready ? "border-emerald-200 bg-emerald-50" : "border-amber-200 bg-amber-50"}`}
               data-testid="drawer-procore-checklist">
            <div className="text-[10px] font-semibold uppercase tracking-wide text-slate-600">
              {ready ? "Ready for Procore" : "Needed before this item can go to Procore"}
            </div>
            <ul className="mt-1 space-y-0.5">
              {checks.map((c) => (
                <li key={c.label} className={`flex items-center gap-1.5 text-[11px] ${
                  c.ok ? "text-slate-500" : "text-amber-800"}`}
                    data-testid={`drawer-check-${c.ok ? "ok" : "missing"}`}>
                  {c.ok ? <Check className="h-3 w-3 text-emerald-600" />
                        : <XIcon className="h-3 w-3 text-amber-600" />}
                  {c.label}
                </li>
              ))}
            </ul>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <Field label="Lead time (days)">
              <input type="number" value={form.lead_time_days}
                     onChange={(e) => set("lead_time_days", e.target.value)}
                     className={inputCls} data-testid="drawer-lead-time" />
            </Field>
            <div className="flex items-end gap-4 pb-1">
              <label className="flex items-center gap-1.5 text-xs text-slate-600">
                <input type="checkbox" checked={!!form.taxable} onChange={(e) => set("taxable", e.target.checked)}
                       data-testid="drawer-taxable" /> Taxable
              </label>
              <label className="flex items-center gap-1.5 text-xs text-slate-600">
                <input type="checkbox" checked={!!form.active} onChange={(e) => set("active", e.target.checked)}
                       data-testid="drawer-active" /> Active
              </label>
            </div>
          </div>

          <Field label="Notes">
            <textarea value={form.notes} onChange={(e) => set("notes", e.target.value)} rows={2}
                      className="mt-1 w-full rounded-md border border-slate-200 px-2 py-1 text-xs"
                      data-testid="drawer-notes" />
          </Field>

          {!isNew && item?.manual_edits?.length > 0 && (
            <p className="text-[10px] text-slate-400">
              Import-protected fields: {item.manual_edits.join(", ")}
            </p>
          )}

          <div className="flex justify-end gap-2 pt-2 pb-6">
            <Button variant="outline" size="sm" onClick={() => onOpenChange(false)}
                    data-testid="drawer-cancel-btn">Cancel</Button>
            <Button size="sm" onClick={save} disabled={saving || !ready} data-testid="drawer-save-btn">
              {saving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              {isNew ? "Create item" : "Save changes"}
            </Button>
          </div>
        </div>
      </SheetContent>
    </Sheet>
  );
}
